import { useCallback, useEffect, useRef } from "react";
import { InfoMessageType } from "src/SharedComponents/InfoBar/InfoBar";
import { useAuraSupportInfo } from "src/Utils/Hooks/UseAuraInfoContext";
import { useChangeSupportUserRole } from "./useChangeSupportUserRole";
import { SupportRoles } from "./useGetAuraSupportInfo";
import { logger } from "src/Utils/LoggerService/LoggerService";

export const useChangeSupportUserRoleWrapper = () => {
    const { changeRole, changeRoleResponse, changeRoleError, changeRoleLoading } = useChangeSupportUserRole();
    const { adminDetails, setAdminInfo, setInfobar, setLoader } = useAuraSupportInfo();
    const pendingUserId = useRef<string>('');
    const pendingRole = useRef<SupportRoles | null>(null);

    useEffect(() => {
        setLoader(changeRoleLoading);
    }, [changeRoleLoading, setLoader]);

    useEffect(() => {
        if (changeRoleResponse && pendingRole.current && pendingUserId.current && adminDetails) {
            const newRole = pendingRole.current;
            const userId = pendingUserId.current;
            logger.logInformation(`Aura Support user ${userId} role changed to ${newRole}`);
            setAdminInfo({
                ...adminDetails,
                users: adminDetails.users.map((u) => (u.userId === userId ? { ...u, role: newRole } : u))
            });
            setInfobar({ message: `Role updated to ${newRole}.`, messageType: InfoMessageType.success });
            pendingRole.current = null;
            pendingUserId.current = '';
        }
    }, [changeRoleResponse, adminDetails, setAdminInfo, setInfobar]);

    useEffect(() => {
        if (changeRoleError) {
            logger.logError(`Failed to change role for user ${pendingUserId.current}: ${changeRoleError}`);
            setInfobar({ message: changeRoleError, messageType: InfoMessageType.error });
            pendingRole.current = null;
            pendingUserId.current = '';
        }
    }, [changeRoleError, setInfobar]);

    const updateRole = useCallback((userId: string, role: SupportRoles) => {
        pendingUserId.current = userId;
        pendingRole.current = role;
        changeRole(userId, role);
    }, [changeRole]);

    return { updateRole };
};
